import { SortableColumn } from '@/components/SortableTable/components/SortableColumn';
import { sortRules } from '@/components/Table/hooks';
import { EnumSortDirection, SortDirection } from '@/components/Table/types';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { RowDef } from '@/components/TableV2/TableV2';
import { useCallback, useMemo, useState } from 'react';

export type TableSorting<T> = {
  accessor: keyof T;
  direction: SortDirection;
};

type UseTableSortProps<T> = {
  columns: SortableColumnDef<T>[];
  rows: RowDef<T>[];
  initialSort?: TableSorting<T>[];
  rowSort?: boolean;
  onSort?: (sorts: TableSorting<T>[]) => void;
};

export const useTableSort = <T,>({
  initialSort,
  rows,
  rowSort,
  onSort,
  columns,
}: UseTableSortProps<T>) => {
  const [sorts, setSorts] = useState<TableSorting<T>[]>(initialSort ?? []);

  const sortedRows = useMemo(() => {
    return rowSort
      ? rows.sort((r1, r2) => {
          for (const sort of sorts) {
            const sortResult = sortRules[sort.direction](
              r1[sort.accessor],
              r2[sort.accessor],
            );

            if (sortResult !== 0) {
              return sortResult;
            }
          }

          return 0;
        })
      : rows;
  }, [rowSort, rows, sorts]);

  const handleSort = useCallback(
    (columnAccessor: keyof T) => {
      const sort = sorts.find((s) => s.accessor === columnAccessor);
      let columnSort;
      if (!sort) {
        columnSort = [
          { accessor: columnAccessor, direction: EnumSortDirection.ascending },
        ];
      } else if (sort.direction === EnumSortDirection.descending) {
        columnSort = sorts.filter((s) => s.accessor !== columnAccessor);
      } else {
        columnSort = sorts.map((s) =>
          s.accessor === columnAccessor
            ? { ...s, direction: EnumSortDirection.descending }
            : s,
        );
      }

      setSorts(columnSort);
      onSort?.(columnSort);
    },
    [onSort, sorts],
  );

  const sortableColumns = useMemo(() => {
    return columns.map((column) => {
      if (column.sortable === false) {
        return column;
      }
      const sortRender = () => {
        return (
          <SortableColumn
            key={column.id}
            column={column}
            onSort={() => column.accessor && handleSort(column.accessor)}
            sortDirection={
              sorts.find((sort) => sort.accessor === column.accessor)?.direction
            }
          />
        );
      };

      return { ...column, render: sortRender };
    });
  }, [columns, handleSort, sorts]);

  return {
    rows: sortedRows,
    columns: sortableColumns,
  };
};
